import React from 'react';

import FFWebTheme from 'ff/debrief/common/StyledComponentThemes';
import TextField from '@mui/material/TextField';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import { useField } from 'formik';

import { TailNumber } from 'ff/debrief/models';
import { useGetRecentTailNumbersQuery } from 'ff/debrief/features/TracklogApi';

const filter = createFilterOptions<TailNumber>();

interface Props {
    value: TailNumber;
    onChange: (newValue: TailNumber) => void;
}

const TailNumberField = (props: React.PropsWithChildren<Props>) => {
    const { data: recentTails, isLoading } = useGetRecentTailNumbersQuery(null);
    const [, meta] = useField('tailNumber');

    const isUnknown = props.value?.tailNumber === 'Unknown';
    const hasError = meta.error;

    return (
        <Autocomplete
            sx={{ pr: 2, flexGrow: 1 }}
            options={
                recentTails?.map(t => ({ label: t.tailNumber, ...t })) || []
            }
            value={isUnknown ? null : props.value}
            onChange={(evt, newValue) => {
                if (typeof newValue === 'string') {
                    props.onChange({
                        tailNumber: newValue,
                        label: newValue,
                        manufacturer: '',
                        model: '',
                    });
                } else {
                    props.onChange(newValue);
                }
            }}
            filterOptions={(options, params) => {
                const filtered = filter(options, params);
                const { inputValue } = params;
                const isExisting = options.some(
                    option => inputValue === option.tailNumber
                );
                if (inputValue !== '' && !isExisting) {
                    filtered.push({
                        tailNumber: inputValue,
                        label: `Add "${inputValue}"`,
                        manufacturer: '',
                        model: '',
                    });
                }
                return filtered;
            }}
            loading={isLoading}
            disableClearable
            renderInput={params => (
                <TextField
                    {...params}
                    label="Tail Number"
                    variant="standard"
                    placeholder="Search for your tail number"
                    error={!!hasError}
                    helperText={hasError ? meta.error : ''}
                    sx={{
                        input: {
                            color: FFWebTheme.blue,
                            fontWeight: 600,
                        },
                    }}
                />
            )}
        />
    );
};

export default TailNumberField;
